// Test comment added for progress tracking demo
import { debug } from "../lib/debug.js";
// ============================================================================
// ELITE OPERATOR STATUS (EOS) SYSTEM
// ============================================================================
// EOS is a binary elite certification earned through sustained Tier-5 excellence.
// It unlocks A380-triggered rare aircraft rolls with fixed probabilities.
// ============================================================================

// ============================================================================
// EOS REQUIREMENTS
// ============================================================================
// ONE-TIME GATES (must meet to START attempting, don't break streak):
//   - Tier 5 (15,000 rep)
//   - 30+ aircraft
//
// DAILY STREAK METRICS (all 5 must be met for 7 consecutive days):
//   1. 85%+ OTP (7-day rolling)
//   2. Zero cancellations
//   3. Zero safety incidents
//   4. Positive cash flow
//   5. 20+ flights completed
// ============================================================================
export const EOS_REQUIREMENTS = {
  QUALIFICATION_DAYS: 10,        // Consecutive days needed (both Simple & Simulation modes)
  // One-time gates (don't break streak if they drop):
  MIN_REP_SCORE: 15000,          // Gate: Must have Tier 5 to START
  MIN_FLEET_SIZE: 30,            // Gate: Must have 30+ aircraft to START
  // Daily streak metrics (all 5 control the streak counter):
  MIN_OTP_ARRIVALS: 0.85,        // Daily: 85% on-time arrival rate (rolling 7-day)
  MAX_CANCELLATIONS: 0,          // Daily: Zero cancellations allowed
  MAX_SAFETY_INCIDENTS: 0,       // Daily: Zero safety incidents
  MIN_NET_CASHFLOW: 0,           // Daily: Must be positive (≥ $0)
  MIN_FLIGHTS_PER_DAY: 20,       // Daily: Must complete 20+ flights
};

// Note: EOS is PERMANENT once earned in Simple Mode.
// Collapse system is a Simulation Mode feature (not implemented yet).

// ============================================================================
// RARE AIRCRAFT ROLL PROBABILITIES (FIXED, NON-SCALING)
// ============================================================================
export const ROLL_PROBABILITIES = {
  RARE: 0.795,       // 79.5%
  RELIC: 0.10,       // 10.0% - Historical aviation artifacts
  ANOMALOUS: 0.05,   // 5.0% - Strange and unexplained
  LEGENDARY: 0.049,  // 4.9% - Pinnacle of aviation engineering
  MYTHIC: 0.001,     // 0.1% - The impossible made real (ONE per server per month)
};

// ============================================================================
// MYTHIC CLAIM SYSTEM - ONE PER SERVER PER MONTH
// ============================================================================
// The Ghost Apotheosis can only be won ONCE per month across all players.
// Once claimed, it "poofs" from the roll table until next month.
// This creates genuine scarcity and competition.
// ============================================================================

/**
 * Get the current month key for mythic tracking (YYYY-MM format)
 */
export function getCurrentMythicMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Check if the mythic has been claimed this month
 * In production, this would check server state. For now, uses localStorage.
 * @returns {Object|null} Claim info if claimed, null if available
 */
export function getMythicClaimStatus() {
  try {
    const claimData = localStorage.getItem('mythicClaim');
    if (!claimData) return null;

    const claim = JSON.parse(claimData);
    const currentMonth = getCurrentMythicMonth();

    // If the claim is from a different month, the mythic is available again
    if (claim.month !== currentMonth) {
      return null;
    }

    return claim;
  } catch (e) {
    console.error('[EOS] Error reading mythic claim status:', e);
    return null;
  }
}

/**
 * Check if mythic is available to be won this month
 * @returns {boolean} True if Ghost Apotheosis can still be won
 */
export function isMythicAvailable() {
  return getMythicClaimStatus() === null;
}

/**
 * Claim the mythic for this month (called when someone wins it)
 * @param {string} airlineName - Name of the airline that won
 * @param {string} playerId - Player ID who won
 * @returns {Object} The claim record
 */
export function claimMythic(airlineName, playerId) {
  const claim = {
    month: getCurrentMythicMonth(),
    claimedAt: new Date().toISOString(),
    airlineName: airlineName,
    playerId: playerId,
    aircraft: 'Ghost Apotheosis',
  };

  try {
    localStorage.setItem('mythicClaim', JSON.stringify(claim));
    debug(`[EOS] MYTHIC CLAIMED! ${airlineName} has won the Ghost Apotheosis for ${claim.month}`);
  } catch (e) {
    console.error('[EOS] Error saving mythic claim:', e);
  }

  return claim;
}

/**
 * Get info about who claimed the mythic this month (for UI display)
 * @returns {Object|null} Claim info or null if unclaimed
 */
export function getMonthlyMythicWinner() {
  return getMythicClaimStatus();
}

// ============================================================================
// RARE AIRCRAFT DATABASE
// ============================================================================
export const RARE_AIRCRAFT = {
  // RARE TIER (79.5% of rolls) - 10 aircraft
  rare: [
    {
      id: "747-9",
      name: "747-9",
      rarity: "rare",
      capacity: 450,
      range: 8500,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 10000,
      purchasePrice: 0,
      monthlyMaintenance: 500000,
      description: "Stretched 747-8 concept with 500+ seats, Boeing's answer to the A380.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "747-10",
      name: "747-10",
      rarity: "rare",
      capacity: 480,
      range: 9000,
      cruiseSpeed: 495,
      maxAltitude: 43000,
      fuelBurn: 10500,
      purchasePrice: 0,
      monthlyMaintenance: 520000,
      description: "Ultra-long 747 variant, double-deck throughout entire fuselage.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "777-400",
      name: "777-400",
      rarity: "rare",
      capacity: 380,
      range: 8200,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 8500,
      purchasePrice: 0,
      monthlyMaintenance: 450000,
      description: "Extended-range 777 concept with folding wingtips.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "777-500",
      name: "777-500",
      rarity: "rare",
      capacity: 400,
      range: 8800,
      cruiseSpeed: 495,
      maxAltitude: 43000,
      fuelBurn: 9000,
      purchasePrice: 0,
      monthlyMaintenance: 480000,
      description: "Stretched 777X variant designed for ultra-high-capacity routes.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "777-xr8-ed",
      name: "777-XR8-ED",
      rarity: "rare",
      capacity: 420,
      range: 9500,
      cruiseSpeed: 500,
      maxAltitude: 44000,
      fuelBurn: 9200,
      purchasePrice: 0,
      monthlyMaintenance: 500000,
      description: "Experimental 777 with radical efficiency improvements.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "ilyushin-illusion",
      name: "Ilyushin Illusion",
      rarity: "rare",
      capacity: 350,
      range: 7500,
      cruiseSpeed: 470,
      maxAltitude: 41000,
      fuelBurn: 7800,
      purchasePrice: 0,
      monthlyMaintenance: 420000,
      description: "Russian widebody that phases in and out of visibility.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "concord-harmonia",
      name: "Concord Harmonia",
      rarity: "rare",
      capacity: 180,
      range: 5500,
      cruiseSpeed: 1200,
      maxAltitude: 55000,
      fuelBurn: 15000,
      purchasePrice: 0,
      monthlyMaintenance: 800000,
      description: "Spiritual successor to Concorde, Mach 2.2 with 200 seats.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "a400",
      name: "A400",
      rarity: "rare",
      capacity: 360,
      range: 7800,
      cruiseSpeed: 485,
      maxAltitude: 42000,
      fuelBurn: 7500,
      purchasePrice: 0,
      monthlyMaintenance: 430000,
      description: "Airbus concept aircraft, bridge between A380 and future designs.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "a410",
      name: "A410",
      rarity: "rare",
      capacity: 390,
      range: 8200,
      cruiseSpeed: 490,
      maxAltitude: 42000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 460000,
      description: "Advanced Airbus widebody with next-gen engines.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "plane",
    },
    {
      id: "a450",
      name: "A450",
      rarity: "rare",
      capacity: 425,
      range: 8600,
      cruiseSpeed: 495,
      maxAltitude: 43000,
      fuelBurn: 8500,
      purchasePrice: 0,
      monthlyMaintenance: 490000,
      description: "Ultimate Airbus widebody concept, largest of the A4XX family.",
      specialAbilities: ["high_capacity", "flagship"],
      tradeoffs: ["high_operating_cost"],
      icon: "plane",
    },
    // NEW BATCH - Government/Military Rare
    {
      id: "vc-32a",
      name: "VC-32A Air Force Two",
      rarity: "rare",
      capacity: 45,
      range: 5500,
      cruiseSpeed: 530,
      maxAltitude: 42000,
      fuelBurn: 3200,
      purchasePrice: 0,
      monthlyMaintenance: 65000,
      description: "757-200 configured for VP transport. Also serves as backup Air Force One.",
      specialAbilities: ["government_clearance", "priority_routing"],
      tradeoffs: ["high_security_overhead"],
      icon: "shield",
    },
    {
      id: "a340-8000",
      name: "Airbus A340-8000",
      rarity: "rare",
      capacity: 60,
      range: 9000,
      cruiseSpeed: 490,
      maxAltitude: 41000,
      fuelBurn: 6800,
      purchasePrice: 0,
      monthlyMaintenance: 380000,
      description: "VIP ultra-long-range variant. Brunei Royal Flight spec.",
      specialAbilities: ["ultra_long_range", "vip_config"],
      tradeoffs: ["low_capacity"],
      icon: "crown",
    },
    {
      id: "eclipse-500",
      name: "Eclipse 500",
      rarity: "rare",
      capacity: 4,
      range: 1125,
      cruiseSpeed: 370,
      maxAltitude: 41000,
      fuelBurn: 400,
      purchasePrice: 0,
      monthlyMaintenance: 25000,
      description: "The very light jet that started a revolution, then bankruptcy.",
      specialAbilities: ["very_light_jet", "low_operating_cost"],
      tradeoffs: ["tiny_capacity", "limited_range"],
      icon: "feather",
    },
    // === INTERNATIONAL REGIONAL AIRCRAFT ===
    {
      id: "dornier-328",
      name: "Dornier 328",
      rarity: "rare",
      capacity: 33,
      range: 1100,
      cruiseSpeed: 335,
      maxAltitude: 31000,
      fuelBurn: 600,
      purchasePrice: 0,
      monthlyMaintenance: 45000,
      description: "German regional turboprop known for efficiency and reliability on short routes.",
      specialAbilities: ["turboprop_efficiency", "short_field"],
      tradeoffs: ["limited_range"],
      icon: "plane",
    },
    {
      id: "iai-westwind",
      name: "IAI Westwind",
      rarity: "rare",
      capacity: 10,
      range: 2500,
      cruiseSpeed: 542,
      maxAltitude: 45000,
      fuelBurn: 350,
      purchasePrice: 0,
      monthlyMaintenance: 35000,
      description: "Israeli business jet that became a global executive transport icon.",
      specialAbilities: ["executive_transport", "high_speed"],
      tradeoffs: ["tiny_capacity"],
      icon: "briefcase",
    },
    {
      id: "iai-arava",
      name: "IAI Arava",
      rarity: "rare",
      capacity: 24,
      range: 800,
      cruiseSpeed: 198,
      maxAltitude: 25000,
      fuelBurn: 300,
      purchasePrice: 0,
      monthlyMaintenance: 30000,
      description: "Israeli STOL utility aircraft built for rough field operations.",
      specialAbilities: ["stol", "rough_field"],
      tradeoffs: ["slow_cruise", "limited_range"],
      icon: "truck",
    },
    {
      id: "piaggio-p180",
      name: "Piaggio P.180 Avanti",
      rarity: "rare",
      capacity: 9,
      range: 1800,
      cruiseSpeed: 398,
      maxAltitude: 41000,
      fuelBurn: 280,
      purchasePrice: 0,
      monthlyMaintenance: 40000,
      description: "Italian pusher-prop with forward canards, fastest turboprop ever built.",
      specialAbilities: ["turboprop_speed", "fuel_efficient"],
      tradeoffs: ["tiny_capacity"],
      icon: "zap",
    },
    {
      id: "mitsubishi-spacejet",
      name: "Mitsubishi SpaceJet",
      rarity: "rare",
      capacity: 88,
      range: 2000,
      cruiseSpeed: 515,
      maxAltitude: 39000,
      fuelBurn: 1200,
      purchasePrice: 0,
      monthlyMaintenance: 85000,
      description: "Japan's ambitious regional jet program, cancelled after billions invested.",
      specialAbilities: ["modern_regional", "fuel_efficient"],
      tradeoffs: ["never_certified"],
      icon: "sunset",
    },
    {
      id: "mitsubishi-mu2",
      name: "Mitsubishi MU-2",
      rarity: "rare",
      capacity: 12,
      range: 1400,
      cruiseSpeed: 340,
      maxAltitude: 29000,
      fuelBurn: 350,
      purchasePrice: 0,
      monthlyMaintenance: 35000,
      description: "Japanese high-speed turboprop with distinctive tip tanks.",
      specialAbilities: ["high_speed_turboprop"],
      tradeoffs: ["demanding_handling"],
      icon: "plane",
    },
    {
      id: "an-148",
      name: "Antonov AN-148",
      rarity: "rare",
      capacity: 80,
      range: 2200,
      cruiseSpeed: 475,
      maxAltitude: 40000,
      fuelBurn: 1400,
      purchasePrice: 0,
      monthlyMaintenance: 75000,
      description: "Ukrainian regional jet with high-wing design for rough runways.",
      specialAbilities: ["rough_field", "high_wing"],
      tradeoffs: ["limited_parts"],
      icon: "plane",
    },
    {
      id: "an-158",
      name: "Antonov AN-158",
      rarity: "rare",
      capacity: 99,
      range: 2500,
      cruiseSpeed: 475,
      maxAltitude: 40000,
      fuelBurn: 1500,
      purchasePrice: 0,
      monthlyMaintenance: 80000,
      description: "Stretched Ukrainian regional jet for higher capacity routes.",
      specialAbilities: ["rough_field", "high_capacity_regional"],
      tradeoffs: ["limited_parts"],
      icon: "plane",
    },
    {
      id: "casa-c212",
      name: "CASA C-212 Aviocar",
      rarity: "rare",
      capacity: 26,
      range: 900,
      cruiseSpeed: 190,
      maxAltitude: 26000,
      fuelBurn: 280,
      purchasePrice: 0,
      monthlyMaintenance: 28000,
      description: "Spanish STOL utility aircraft for remote and unpaved strips.",
      specialAbilities: ["stol", "rough_field", "utility"],
      tradeoffs: ["slow_cruise"],
      icon: "truck",
    },
    {
      id: "hal-regional",
      name: "HAL Regional Transport",
      rarity: "rare",
      capacity: 50,
      range: 1000,
      cruiseSpeed: 280,
      maxAltitude: 25000,
      fuelBurn: 600,
      purchasePrice: 0,
      monthlyMaintenance: 45000,
      description: "Indian-built turboprop for domestic regional routes.",
      specialAbilities: ["regional_workhorse"],
      tradeoffs: ["limited_range"],
      icon: "plane",
    },
    {
      id: "kai-kc100",
      name: "KAI KC-100 Naraon",
      rarity: "rare",
      capacity: 4,
      range: 700,
      cruiseSpeed: 220,
      maxAltitude: 20000,
      fuelBurn: 80,
      purchasePrice: 0,
      monthlyMaintenance: 15000,
      description: "South Korean trainer and light utility aircraft.",
      specialAbilities: ["trainer", "low_cost"],
      tradeoffs: ["tiny_capacity", "limited_range"],
      icon: "feather",
    },
    {
      id: "saab-90",
      name: "Saab 90 Scandia",
      rarity: "rare",
      capacity: 32,
      range: 1500,
      cruiseSpeed: 285,
      maxAltitude: 24000,
      fuelBurn: 500,
      purchasePrice: 0,
      monthlyMaintenance: 40000,
      description: "Swedish post-war airliner, Saab's first commercial aircraft.",
      specialAbilities: ["historic_european"],
      tradeoffs: ["vintage_tech"],
      icon: "history",
    },
    {
      id: "iptn-n219",
      name: "IPTN N219",
      rarity: "rare",
      capacity: 19,
      range: 600,
      cruiseSpeed: 215,
      maxAltitude: 22000,
      fuelBurn: 250,
      purchasePrice: 0,
      monthlyMaintenance: 25000,
      description: "Indonesian utility aircraft for island-hopping routes.",
      specialAbilities: ["island_hopper", "stol"],
      tradeoffs: ["limited_range", "slow_cruise"],
      icon: "anchor",
    },
  ],

  // RELIC TIER (10% of rolls) - Historical aviation artifacts
  relic: [
    {
      id: "dc-3-heritage",
      name: "DC-3 Heritage",
      rarity: "relic",
      capacity: 32,
      range: 1500,
      cruiseSpeed: 207,
      maxAltitude: 24000,
      fuelBurn: 400,
      purchasePrice: 0,
      monthlyMaintenance: 60000,
      description: "Restored golden age icon, the plane that made commercial aviation viable.",
      specialAbilities: ["historic_icon", "reliable"],
      tradeoffs: ["vintage_tech", "slow_cruise"],
      icon: "history",
    },
    {
      id: "spirit-of-st-louis",
      name: "Spirit of St. Louis",
      rarity: "relic",
      capacity: 1,
      range: 4000, // Extreme for its class
      cruiseSpeed: 110,
      maxAltitude: 15000,
      fuelBurn: 50,
      purchasePrice: 0,
      monthlyMaintenance: 50000,
      description: "Single-seat. Extreme range modifier for its class. No copilots, no automation. Bonuses tied to navigation difficulty and exploration. A 'first principles' plane.",
      specialAbilities: ["extreme_range_modifier", "exploration_bonus", "navigation_challenge"],
      tradeoffs: ["no_copilot", "no_automation", "single_seat"],
      icon: "compass",
    },
    {
      id: "hughes-h4-hercules",
      name: "Hughes H-4 Hercules (Spruce Goose)",
      rarity: "relic",
      capacity: 750, // Enormous
      range: 3000,
      cruiseSpeed: 175,
      maxAltitude: 5000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 1500000,
      description: "Enormous capacity modifier. Severe slot and runway penalties. Water-only logic. Exists to teach scale and absurdity.",
      specialAbilities: ["enormous_capacity", "water_operations"],
      tradeoffs: ["water_only", "severe_slot_penalty", "runway_length_extreme"],
      icon: "ship",
    },
    {
      id: "douglas-dc8-ship-one",
      name: "Douglas DC-8 'Ship One'",
      rarity: "relic",
      capacity: 176,
      range: 6000,
      cruiseSpeed: 520,
      maxAltitude: 42000,
      fuelBurn: 5500,
      purchasePrice: 0,
      monthlyMaintenance: 300000,
      description: "First of type flag. Minor efficiency bonuses to early jet routes. Prestige multiplier, not profit.",
      specialAbilities: ["first_of_type", "early_jet_bonus", "prestige_multiplier"],
      tradeoffs: [],
      icon: "flag",
    },
    {
      id: "enola-gay",
      name: "Enola Gay",
      rarity: "relic",
      capacity: 235,
      range: 235,
      cruiseSpeed: 235,
      maxAltitude: 235,
      fuelBurn: 235,
      purchasePrice: 0,
      monthlyMaintenance: 235,
      description: "All stats = 235. No special bonuses. No penalties. Pure symbolic artifact. The silence is the design.",
      specialAbilities: [],
      tradeoffs: [],
      icon: "monument",
    },
    {
      id: "lockheed-electra-10e",
      name: "Lockheed Model 10-E Electra",
      rarity: "relic",
      capacity: 1,
      range: 700,
      cruiseSpeed: 190,
      maxAltitude: 19000,
      fuelBurn: 200,
      purchasePrice: 0,
      monthlyMaintenance: 80000,
      description: "No modern nav systems. Player receives only a map and compass. Route uncertainty increases. May not return from long overwater flights. Beautifully cruel.",
      specialAbilities: ["map_and_compass_only", "fog_of_war", "discovery_bonus"],
      tradeoffs: ["no_modern_nav", "route_uncertainty", "overwater_risk"],
      icon: "map",
    },
    {
      id: "wright-flyer",
      name: "Wright Flyer",
      rarity: "relic",
      capacity: 1,
      range: 1, // Cannot leave local tile
      cruiseSpeed: 30,
      maxAltitude: 30,
      fuelBurn: 0,
      purchasePrice: 0,
      monthlyMaintenance: 10000,
      description: "PAX: 1. Flight time: 2 minutes. Cannot leave local tile. Essentially a museum piece that technically flies. Perfect.",
      specialAbilities: ["museum_piece", "first_flight"],
      tradeoffs: ["local_tile_only", "two_minute_max"],
      icon: "history",
    },
    {
      id: "douglas-dc3-dakota",
      name: "Douglas DC-3 'Dakota'",
      rarity: "relic",
      capacity: 32,
      range: 1500,
      cruiseSpeed: 180,
      maxAltitude: 23000,
      fuelBurn: 300,
      purchasePrice: 0,
      monthlyMaintenance: 60000,
      description: "Reliability monster. Immune to certain failure types. Low revenue ceiling, ultra-stable. Teaches longevity.",
      specialAbilities: ["reliability_monster", "failure_immunity", "ultra_stable"],
      tradeoffs: ["low_revenue_ceiling"],
      icon: "shield",
    },
    {
      id: "benoist-xiv",
      name: "Benoist XIV Flying Boat",
      rarity: "relic",
      capacity: 1,
      range: 30, // Extremely short
      cruiseSpeed: 64,
      maxAltitude: 500,
      fuelBurn: 20,
      purchasePrice: 0,
      monthlyMaintenance: 25000,
      description: "Water-only. Extremely short range. First commercial flight flag. Unlocks early seaplane logic. Pure aviation history flex.",
      specialAbilities: ["first_commercial_flight", "seaplane_logic", "history_flex"],
      tradeoffs: ["water_only", "extremely_short_range"],
      icon: "anchor",
    },
    {
      id: "dh106-comet",
      name: "de Havilland DH.106 Comet",
      rarity: "relic",
      capacity: 44,
      range: 2400,
      cruiseSpeed: 460,
      maxAltitude: 40000,
      fuelBurn: 3500,
      purchasePrice: 0,
      monthlyMaintenance: 200000,
      description: "First jet airliner. High speed for era. Structural risk modifier. Teaches 'innovation has costs'.",
      specialAbilities: ["first_jet_airliner", "high_speed_era"],
      tradeoffs: ["structural_risk", "innovation_cost"],
      icon: "zap",
    },
    {
      id: "boeing-707",
      name: "Boeing 707",
      rarity: "relic",
      capacity: 189,
      range: 5750,
      cruiseSpeed: 525,
      maxAltitude: 41000,
      fuelBurn: 4800,
      purchasePrice: 0,
      monthlyMaintenance: 250000,
      description: "Transitional aircraft. Bridges piston to jet economy. Moderate everything, nothing extreme. Closes the historical chapter cleanly.",
      specialAbilities: ["transitional_bridge", "piston_to_jet"],
      tradeoffs: [],
      icon: "book",
    },
    // NEW BATCH - Presidential & Government Relics
    {
      id: "vc-25a",
      name: "VC-25A Air Force One",
      rarity: "relic",
      capacity: 76,
      range: 7800,
      cruiseSpeed: 580,
      maxAltitude: 45100,
      fuelBurn: 9200,
      purchasePrice: 0,
      monthlyMaintenance: 2100000,
      description: "SAM 28000/29000. The flying White House. 4,000 sq ft of interior space, mid-air refueling capable, hardened against EMP.",
      specialAbilities: ["flying_white_house", "emp_hardened", "aerial_refueling"],
      tradeoffs: ["extreme_security", "massive_overhead"],
      icon: "flag",
    },
    {
      id: "vc-137c",
      name: "VC-137C SAM 26000",
      rarity: "relic",
      capacity: 40,
      range: 5750,
      cruiseSpeed: 525,
      maxAltitude: 41000,
      fuelBurn: 5200,
      purchasePrice: 0,
      monthlyMaintenance: 850000,
      description: "The 707 that carried JFK to Dallas and his body home. LBJ sworn in aboard. Nixon to China. Reagan to Reykjavik.",
      specialAbilities: ["historic_significance", "presidential_history"],
      tradeoffs: ["museum_piece"],
      icon: "monument",
    },
    {
      id: "vc-121e",
      name: "VC-121E Columbine III",
      rarity: "relic",
      capacity: 24,
      range: 4600,
      cruiseSpeed: 340,
      maxAltitude: 25000,
      fuelBurn: 2800,
      purchasePrice: 0,
      monthlyMaintenance: 450000,
      description: "Eisenhower's Constellation. First aircraft to carry 'Air Force One' callsign. Named for Colorado state flower.",
      specialAbilities: ["first_air_force_one", "constellation_elegance"],
      tradeoffs: ["prop_era", "low_altitude"],
      icon: "star",
    },
    {
      id: "vc-54c",
      name: "VC-54C Sacred Cow",
      rarity: "relic",
      capacity: 12,
      range: 3900,
      cruiseSpeed: 280,
      maxAltitude: 22000,
      fuelBurn: 1200,
      purchasePrice: 0,
      monthlyMaintenance: 250000,
      description: "The first presidential aircraft. FDR to Yalta, 1945. Battery-powered elevator for wheelchair access.",
      specialAbilities: ["first_presidential_aircraft", "historic_pioneer"],
      tradeoffs: ["prop_era", "extremely_slow"],
      icon: "history",
    },
    {
      id: "tu-144",
      name: "Tupolev Tu-144 Charger",
      rarity: "relic",
      capacity: 140,
      range: 3500,
      cruiseSpeed: 1320,
      maxAltitude: 59000,
      fuelBurn: 22000,
      purchasePrice: 0,
      monthlyMaintenance: 1500000,
      description: "The Soviet Concorde. First SST to fly. Canards, unreliable, spectacular. Only 55 passenger flights ever.",
      specialAbilities: ["supersonic", "soviet_engineering"],
      tradeoffs: ["unreliable", "fuel_monster", "short_range"],
      icon: "zap",
    },
    {
      id: "an-225",
      name: "Antonov An-225 Mriya",
      rarity: "relic",
      capacity: 0,
      range: 4000,
      cruiseSpeed: 458,
      maxAltitude: 36000,
      fuelBurn: 15000,
      purchasePrice: 0,
      monthlyMaintenance: 2500000,
      description: "The largest aircraft ever built. One of one. Destroyed 2022. 640 ton MTOW. Six engines. Dreams made manifest.",
      specialAbilities: ["largest_ever", "massive_cargo", "one_of_one"],
      tradeoffs: ["destroyed_2022", "cargo_only", "extreme_fuel"],
      icon: "mountain",
    },
    {
      id: "concorde",
      name: "Aerospatiale-BAC Concorde",
      rarity: "relic",
      capacity: 100,
      range: 4500,
      cruiseSpeed: 1350,
      maxAltitude: 60000,
      fuelBurn: 18000,
      purchasePrice: 0,
      monthlyMaintenance: 1800000,
      description: "The only successful SST. NYC to London in 3.5 hours. Retired 2003. Still the fastest passenger jet.",
      specialAbilities: ["supersonic_legend", "time_machine", "premium_experience"],
      tradeoffs: ["sonic_boom_restricted", "extreme_fuel", "retired_forever"],
      icon: "flame",
    },
    {
      id: "super-guppy",
      name: "Aero Spacelines Super Guppy",
      rarity: "relic",
      capacity: 0,
      range: 1500,
      cruiseSpeed: 250,
      maxAltitude: 25000,
      fuelBurn: 2500,
      purchasePrice: 0,
      monthlyMaintenance: 600000,
      description: "Swings open at the nose. Carried Saturn V rocket stages. The weird uncle of aviation.",
      specialAbilities: ["outsize_cargo", "nose_swing", "nasa_heritage"],
      tradeoffs: ["slow", "short_range", "specialized"],
      icon: "rocket",
    },
    {
      id: "spruce-goose-relic",
      name: "Hughes H-4 Hercules (Original)",
      rarity: "relic",
      capacity: 750,
      range: 3000,
      cruiseSpeed: 175,
      maxAltitude: 5000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 1500000,
      description: "The actual Spruce Goose. Flew once. 1 mile. 70 feet altitude. Now in a museum. The dream that refused to die.",
      specialAbilities: ["flew_once", "wooden_wonder", "museum_piece"],
      tradeoffs: ["never_flew_again", "water_only", "impractical"],
      icon: "trophy",
    },
    {
      id: "y-10",
      name: "Shanghai Y-10",
      rarity: "relic",
      capacity: 178,
      range: 5560,
      cruiseSpeed: 520,
      maxAltitude: 39000,
      fuelBurn: 5800,
      purchasePrice: 0,
      monthlyMaintenance: 400000,
      description: "China's first jet airliner. Cancelled 1985. Only 2 built. The road not taken.",
      specialAbilities: ["chinese_pioneer", "what_if"],
      tradeoffs: ["cancelled_program", "no_spare_parts"],
      icon: "map",
    },
    {
      id: "n250",
      name: "IPTN N-250 Gatotkaca",
      rarity: "relic",
      capacity: 64,
      range: 1500,
      cruiseSpeed: 330,
      maxAltitude: 25000,
      fuelBurn: 1200,
      purchasePrice: 0,
      monthlyMaintenance: 200000,
      description: "Indonesian turboprop. Asian financial crisis killed it. Only 2 prototypes. National pride, buried by economics.",
      specialAbilities: ["indonesian_dream", "turboprop_efficiency"],
      tradeoffs: ["cancelled_1997", "no_production"],
      icon: "sunset",
    },
  ],

  // ANOMALOUS TIER (5% of rolls) - Strange and unexplained
  // =========================================================================
  // GLOBAL ANOMALOUS RULES:
  // - Are never optimal
  // - May actively harm the airline
  // - Are never required
  // - No tutorials
  // - No warnings beyond vague flavor text
  // - Players are allowed to ruin their own day
  //
  // "Anomalous aircraft intentionally violate normal gameplay expectations
  // and may cause self-inflicted harm; they exist for chaos, lore, and
  // player stories, not optimization."
  // =========================================================================
  anomalous: [
    {
      id: "agee-whells",
      name: "The Agee Whells",
      rarity: "anomalous",
      capacity: 200,
      range: 6000,
      cruiseSpeed: 450,
      maxAltitude: 40000,
      fuelBurn: 5000,
      purchasePrice: 0,
      monthlyMaintenance: 400000,
      description: "Time moves strangely around this aircraft. Pilots report déjà vu. Dispatchers report missing flights.",
      specialAbilities: [
        "temporal_sabotage",      // When launched: ALL other airborne flights reverse course
      ],
      tradeoffs: [
        "flight_reversal",        // All other flights return to origin, revenue lost
        "no_refunds",             // No insurance, no undo
        "chaos_incarnate",        // Pure punishment for careless activation
      ],
      // Player emotion: "Why did I launch this."
      // Named for HG Wells
      icon: "clock",
    },
    {
      id: "vtonly",
      name: "VTOnly",
      rarity: "anomalous",
      capacity: 50,
      range: 0,                   // Cannot travel horizontally
      cruiseSpeed: 0,
      maxAltitude: 60000,         // Goes VERY high though
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 350000,
      description: "Vertical-only aviation. Origin and destination must be identical. Consumes time, crew, and fuel anyway.",
      specialAbilities: [
        "vertical_only",          // Can only fly vertically
      ],
      tradeoffs: [
        "same_airport_only",      // Origin = Destination required
        "zero_revenue",           // Generates nothing
        "resource_waste",         // Consumes fuel, crew time, slot
      ],
      // Player emotion: "This is technically flying."
      // Satire of pointless engineering
      icon: "arrow-up",
    },
    {
      id: "the-bermudan",
      name: "The Bermudan",
      rarity: "anomalous",
      capacity: 180,
      range: 4500,
      cruiseSpeed: 420,
      maxAltitude: 38000,
      fuelBurn: 4800,
      purchasePrice: 0,
      monthlyMaintenance: 380000,
      description: "Filed flight plans are... suggestions. This aircraft has a mind of its own. Destination unknown.",
      specialAbilities: [
        "route_corruption",       // Always diverts to Bermuda Triangle
      ],
      tradeoffs: [
        "destination_override",   // Never arrives at intended destination
        "unexplained_delays",     // Causes odd logs and delays
        "unreliable_returns",     // Return flights are unpredictable
      ],
      // Player emotion: "I KNOW I didn't file that flight plan."
      // Myth-as-mechanic - breaks player trust in routing
      icon: "triangle",
    },
    {
      id: "the-moonshiner",
      name: "The Moonshiner",
      rarity: "anomalous",
      capacity: 80,
      range: 2500,
      cruiseSpeed: 380,
      maxAltitude: 28000,
      fuelBurn: 3000,             // Moonshine burn rate
      purchasePrice: 0,
      monthlyMaintenance: 200000,
      description: "Only flies red-eye. Runs on moonshine. You'll need to find your own supply. Normal airports can't help you.",
      specialAbilities: [
        "red_eye_only",           // Only operates overnight flights
        "moonshine_powered",      // Special fuel type required
        "modest_revenue",         // Actually generates income if you pull it off
      ],
      tradeoffs: [
        "special_fuel_sourcing",  // Must source moonshine via special means
        "no_normal_fueling",      // Cannot refuel at standard airports
        "logistical_nightmare",   // High operational complexity
      ],
      // Player emotion: "This is stupid. I love it."
      // Marginally useful if you lean into the insanity
      icon: "moon",
    },
    {
      id: "spruce-moose",
      name: "Spruce Moose",
      rarity: "anomalous",
      capacity: 0,
      range: 0,
      cruiseSpeed: 0,
      maxAltitude: 0,
      fuelBurn: 0,
      purchasePrice: 0,
      monthlyMaintenance: 1000,
      description: "I said hop in!",
      specialAbilities: ["collectible_artifact"],
      tradeoffs: ["not_functional", "model_scale_only"],
      // Player emotion: "Why is this in my hangar."
      // The Simpsons reference - a model plane that exists purely for comedy
      icon: "trophy",
    },
    // NEW BATCH - More Anomalous Aircraft
    {
      id: "quantum-hopper",
      name: "Quantum Hopper",
      rarity: "anomalous",
      capacity: 150,
      range: 9999,
      cruiseSpeed: 9999,
      maxAltitude: 99000,
      fuelBurn: 1,
      purchasePrice: 0,
      monthlyMaintenance: 999999,
      description: "Exists in superposition. May arrive before departure. May not arrive at all. Schrödinger's airline.",
      specialAbilities: ["quantum_tunneling", "temporal_anomaly"],
      tradeoffs: ["reality_unstable", "insurance_void"],
      icon: "atom",
    },
    {
      id: "the-mobius",
      name: "The Möbius",
      rarity: "anomalous",
      capacity: 200,
      range: 0,
      cruiseSpeed: 400,
      maxAltitude: 35000,
      fuelBurn: 4000,
      purchasePrice: 0,
      monthlyMaintenance: 300000,
      description: "Always returns to origin. Cannot reach destination. Infinite loop. Passengers age but never arrive.",
      specialAbilities: ["infinite_loop", "return_to_origin"],
      tradeoffs: ["no_destination", "existential_dread"],
      icon: "infinity",
    },
    {
      id: "schrodinger-jet",
      name: "Schrödinger Jet",
      rarity: "anomalous",
      capacity: -1,
      range: 5000,
      cruiseSpeed: 500,
      maxAltitude: 40000,
      fuelBurn: 5000,
      purchasePrice: 0,
      monthlyMaintenance: 500000,
      description: "Passenger count is negative. Revenue is negative. Somehow still profitable? Don't open the cargo hold.",
      specialAbilities: ["negative_capacity", "paradox_profit"],
      tradeoffs: ["dont_observe", "cat_situation"],
      icon: "help-circle",
    },
    {
      id: "negative-one",
      name: "Negative One",
      rarity: "anomalous",
      capacity: -1,
      range: -1000,
      cruiseSpeed: -100,
      maxAltitude: -1000,
      fuelBurn: -1000,
      purchasePrice: 0,
      monthlyMaintenance: -100000,
      description: "All stats negative. GENERATES fuel. Time flows backward. Mathematically impossible. Exists anyway.",
      specialAbilities: ["negative_physics", "fuel_generator"],
      tradeoffs: ["backwards_time", "math_error"],
      icon: "minus",
    },
    {
      id: "ghost-freight",
      name: "Ghost Freight",
      rarity: "anomalous",
      capacity: 0,
      range: 15000,
      cruiseSpeed: 600,
      maxAltitude: 50000,
      fuelBurn: 0,
      purchasePrice: 0,
      monthlyMaintenance: 0,
      description: "No crew required. No fuel required. No maintenance. Carries nothing. Goes everywhere. What is it carrying?",
      specialAbilities: ["autonomous", "zero_cost", "phantom_cargo"],
      tradeoffs: ["no_revenue", "what_is_it_doing"],
      icon: "ghost",
    },
    {
      id: "the-perpetual",
      name: "The Perpetual",
      rarity: "anomalous",
      capacity: 250,
      range: 99999,
      cruiseSpeed: 450,
      maxAltitude: 42000,
      fuelBurn: 0,
      purchasePrice: 0,
      monthlyMaintenance: 1000000,
      description: "Never lands. Launched in 1987. Still airborne. Passengers have formed a society. Do not attempt rescue.",
      specialAbilities: ["perpetual_motion", "airborne_society"],
      tradeoffs: ["never_lands", "liability_nightmare"],
      icon: "rotate-cw",
    },
    {
      id: "blackout",
      name: "Blackout",
      rarity: "anomalous",
      capacity: 300,
      range: 8000,
      cruiseSpeed: 520,
      maxAltitude: 45000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 600000,
      description: "Disappears from radar. Disappears from records. Passengers don't remember the flight. Neither do you.",
      specialAbilities: ["radar_invisible", "memory_wipe"],
      tradeoffs: ["untraceable", "plausible_deniability"],
      icon: "eye-off",
    },
    {
      id: "revenue-engine",
      name: "Revenue Engine",
      rarity: "anomalous",
      capacity: 0,
      range: 0,
      cruiseSpeed: 0,
      maxAltitude: 0,
      fuelBurn: 10000,
      purchasePrice: 0,
      monthlyMaintenance: 5000000,
      description: "Not an aircraft. A money printer with wings. Generates cash. Cannot fly. SEC has questions.",
      specialAbilities: ["money_printer", "grounded_profit"],
      tradeoffs: ["cannot_fly", "regulatory_investigation"],
      icon: "dollar-sign",
    },
    {
      id: "yesterday",
      name: "Yesterday",
      rarity: "anomalous",
      capacity: 180,
      range: 4000,
      cruiseSpeed: 480,
      maxAltitude: 39000,
      fuelBurn: 4500,
      purchasePrice: 0,
      monthlyMaintenance: 400000,
      description: "Always arrives yesterday. Schedule it today, it landed yesterday. Passengers remember a flight that hasn't happened yet.",
      specialAbilities: ["temporal_displacement", "arrives_yesterday"],
      tradeoffs: ["causality_violation", "scheduling_nightmare"],
      icon: "rewind",
    },
    {
      id: "the-jinx",
      name: "The Jinx",
      rarity: "anomalous",
      capacity: 200,
      range: 5000,
      cruiseSpeed: 500,
      maxAltitude: 41000,
      fuelBurn: 5000,
      purchasePrice: 0,
      monthlyMaintenance: 350000,
      description: "Every flight causes a random other flight to fail. Success rate: 100%. Collateral damage: Also 100%.",
      specialAbilities: ["guaranteed_success", "random_sabotage"],
      tradeoffs: ["collateral_damage", "fleet_curse"],
      icon: "alert-triangle",
    },
    {
      id: "infinite-loop",
      name: "Infinite Loop",
      rarity: "anomalous",
      capacity: 88,
      range: 888,
      cruiseSpeed: 888,
      maxAltitude: 8888,
      fuelBurn: 888,
      purchasePrice: 0,
      monthlyMaintenance: 88888,
      description: "All stats are 8s. Flies in figure-8 patterns. Takes off at 8:08. Lucky in some cultures. Cursed in this one.",
      specialAbilities: ["figure_eight", "lucky_numbers"],
      tradeoffs: ["pattern_locked", "ocd_trigger"],
      icon: "repeat",
    },
    {
      id: "the-fold",
      name: "The Fold",
      rarity: "anomalous",
      capacity: 400,
      range: 1,
      cruiseSpeed: 999999,
      maxAltitude: 0,
      fuelBurn: 999999,
      purchasePrice: 0,
      monthlyMaintenance: 999999,
      description: "Folds space. Origin and destination become the same point. Instant travel. Infinite fuel cost. Net zero.",
      specialAbilities: ["space_fold", "instant_travel"],
      tradeoffs: ["infinite_cost", "point_collapse"],
      icon: "minimize-2",
    },
    {
      id: "echo-flight",
      name: "Echo Flight",
      rarity: "anomalous",
      capacity: 150,
      range: 4000,
      cruiseSpeed: 450,
      maxAltitude: 38000,
      fuelBurn: 4000,
      purchasePrice: 0,
      monthlyMaintenance: 300000,
      description: "Every flight creates a duplicate flight. Then the duplicate duplicates. Exponential chaos. Cannot be unscheduled.",
      specialAbilities: ["self_replicating", "exponential_flights"],
      tradeoffs: ["cascade_failure", "system_overflow"],
      icon: "copy",
    },
    {
      id: "market-maker",
      name: "Market Maker",
      rarity: "anomalous",
      capacity: 1,
      range: 100,
      cruiseSpeed: 100,
      maxAltitude: 10000,
      fuelBurn: 100,
      purchasePrice: 0,
      monthlyMaintenance: 10000000,
      description: "Affects stock prices of competing airlines. SEC definitely has questions. So does the DOJ.",
      specialAbilities: ["market_manipulation", "competitor_damage"],
      tradeoffs: ["federal_investigation", "prison_risk"],
      icon: "trending-up",
    },
    {
      id: "null-pointer",
      name: "Null Pointer",
      rarity: "anomalous",
      capacity: null,
      range: null,
      cruiseSpeed: null,
      maxAltitude: null,
      fuelBurn: null,
      purchasePrice: 0,
      monthlyMaintenance: null,
      description: "All stats are null. Crashes every system that tries to process it. The IT department quit.",
      specialAbilities: ["system_crash", "undefined_behavior"],
      tradeoffs: ["breaks_everything", "null_reference"],
      icon: "x-circle",
    },
    {
      id: "probability-storm",
      name: "Probability Storm",
      rarity: "anomalous",
      capacity: 200,
      range: 5000,
      cruiseSpeed: 500,
      maxAltitude: 40000,
      fuelBurn: 5000,
      purchasePrice: 0,
      monthlyMaintenance: 500000,
      description: "All outcomes happen simultaneously until observed. Passengers are both satisfied and furious. Revenue is both positive and negative.",
      specialAbilities: ["superposition", "all_outcomes"],
      tradeoffs: ["observer_effect", "uncertain_profit"],
      icon: "cloud-lightning",
    },
    {
      id: "the-debt",
      name: "The Debt",
      rarity: "anomalous",
      capacity: 500,
      range: 10000,
      cruiseSpeed: 600,
      maxAltitude: 45000,
      fuelBurn: 1,
      purchasePrice: 0,
      monthlyMaintenance: 1,
      description: "Perfect stats. Almost free. The catch? Generates compound negative interest. Eventually owns your airline.",
      specialAbilities: ["perfect_stats", "low_cost"],
      tradeoffs: ["compound_debt", "hostile_takeover"],
      icon: "percent",
    },
    {
      id: "passenger-zero",
      name: "Passenger Zero",
      rarity: "anomalous",
      capacity: 1,
      range: 12000,
      cruiseSpeed: 550,
      maxAltitude: 43000,
      fuelBurn: 6000,
      purchasePrice: 0,
      monthlyMaintenance: 800000,
      description: "Carries one passenger. That passenger is always Patient Zero for something. CDC on speed dial.",
      specialAbilities: ["single_passenger", "disease_vector"],
      tradeoffs: ["quarantine_risk", "biohazard"],
      icon: "user-x",
    },
    {
      id: "timeline-fork",
      name: "Timeline Fork",
      rarity: "anomalous",
      capacity: 200,
      range: 6000,
      cruiseSpeed: 500,
      maxAltitude: 41000,
      fuelBurn: 5500,
      purchasePrice: 0,
      monthlyMaintenance: 600000,
      description: "Creates alternate timeline on landing. Original passengers cease to exist here. Alternates take their place. Nobody notices.",
      specialAbilities: ["timeline_split", "passenger_replacement"],
      tradeoffs: ["existential_horror", "identity_crisis"],
      icon: "git-branch",
    },
    {
      id: "zero-divided-by-zero",
      name: "Zero Divided by Zero",
      rarity: "anomalous",
      capacity: 0,
      range: Infinity,
      cruiseSpeed: NaN,
      maxAltitude: NaN,
      fuelBurn: 0,
      purchasePrice: 0,
      monthlyMaintenance: 0,
      description: "Capacity: 0/∞. Can't fly. Appears everywhere. Always crashed. Always fine.",
      specialAbilities: ["mathematical_impossibility", "quantum_superposition"],
      tradeoffs: ["undefined_behavior", "reality_corruption"],
      icon: "divide",
    },
    {
      id: "the-code",
      name: "The Code",
      rarity: "anomalous",
      capacity: 128,
      range: 8080,
      cruiseSpeed: 404,
      maxAltitude: 31337,
      fuelBurn: 256,
      purchasePrice: 0,
      monthlyMaintenance: 0,
      description: "eJwryUhVyCtNzklNLFJIzk9JLVYoLU5NUSjJV0hKVTCAAgD7JwvV",
      specialAbilities: ["arcade_mode", "pixel_perfect"],
      tradeoffs: ["retro_graphics", "high_score_obsession"],
      icon: "gamepad-2",
    },
  ],

  // LEGENDARY TIER (5% of rolls) - Pinnacle of aviation engineering
  legendary: [
    {
      id: "a550-mx-quantumliner",
      name: 'Airbus A550-MX "Quantumliner"',
      rarity: "legendary",
      capacity: 380,
      range: 9200,
      cruiseSpeed: 515,
      maxAltitude: 43000,
      fuelBurn: 7200,
      purchasePrice: 0,
      monthlyMaintenance: 650000,
      description: "AI-optimized flagship that boosts passenger happiness and reduces turnaround inefficiency. Quantum computing flight systems predict and prevent delays before they happen.",
      specialAbilities: [
        "ai_optimization",        // +15% passenger satisfaction
        "turnaround_efficiency",  // -20% turnaround time
        "predictive_maintenance", // Warns of failures 48h early
      ],
      tradeoffs: [
        "software_dependency",    // Requires monthly software updates or degrades
      ],
      icon: "cpu",
    },
    {
      id: "797-9000-cumulus",
      name: 'Boeing 797-9000 "CUMULUS"',
      rarity: "legendary",
      capacity: 320,
      range: 12000, // Ultra-long-range
      cruiseSpeed: 530,
      maxAltitude: 51000, // Ultra-high-altitude
      fuelBurn: 6800,
      purchasePrice: 0,
      monthlyMaintenance: 720000,
      description: "Ultra-high-altitude, ultra-long-range aircraft that dominates premium long-haul routes. Cruises above weather and traffic in the stratospheric sweet spot.",
      specialAbilities: [
        "altitude_dominance",     // Ignores weather below FL450
        "ultra_long_range",       // Can fly any route non-stop
        "premium_route_bonus",    // +25% revenue on 8000+ nm routes
      ],
      tradeoffs: [
        "altitude_restricted_airports", // Cannot serve airports above 8000ft elevation
      ],
      icon: "cloud",
    },
    {
      id: "cx-fury-concorde",
      name: 'Fire Dragon Concorde "CX-Fury"',
      rarity: "legendary",
      capacity: 128,
      range: 5800,
      cruiseSpeed: 1350, // Mach 2+
      maxAltitude: 60000,
      fuelBurn: 18000, // Thirsty beast
      purchasePrice: 0,
      monthlyMaintenance: 1200000,
      description: "Mach-2 commercial hot-rod generating extreme fares at the cost of heat, noise, and attention. Regulators watch this one closely.",
      specialAbilities: [
        "supersonic_speed",       // 2.5x normal cruise speed
        "extreme_premium_fares",  // 3x ticket prices, 90% load factor
        "time_machine",           // Arrive before you left (timezone math)
      ],
      tradeoffs: [
        "sonic_boom_restrictions", // Cannot fly supersonic over land
        "regulatory_attention",    // Random inspections, fines possible
        "heat_signature",          // Higher wear rate on airframe
        "fuel_guzzler",            // 2.5x normal fuel burn
      ],
      icon: "flame",
    },
    {
      id: "serpentis-vii-777",
      name: 'Sky Serpent 777 "Serpentis VII"',
      rarity: "legendary",
      capacity: 350,
      range: 8800,
      cruiseSpeed: 505,
      maxAltitude: 43000,
      fuelBurn: 7000,
      purchasePrice: 0,
      monthlyMaintenance: 580000,
      description: "Crew-optimized widebody with wind-piercing efficiency and unique operational smoothness. Pilots request this aircraft. Passengers don't know why they feel calmer.",
      specialAbilities: [
        "crew_favorite",          // +20% crew satisfaction, reduces fatigue
        "wind_piercing",          // -15% fuel burn in headwinds
        "smooth_operations",      // -30% delay propagation
        "passenger_calm",         // +10% satisfaction even in turbulence
      ],
      tradeoffs: [],
      icon: "wind",
    },
    // NEW BATCH - More Legendary Aircraft
    {
      id: "vc-25b",
      name: "VC-25B Air Force One (New)",
      rarity: "legendary",
      capacity: 70,
      range: 8500,
      cruiseSpeed: 590,
      maxAltitude: 43000,
      fuelBurn: 8800,
      purchasePrice: 0,
      monthlyMaintenance: 2250000,
      description: "The new Air Force One. 747-8 based, entering service 2024. Most advanced airborne command post ever built.",
      specialAbilities: ["next_gen_command_post", "aerial_refueling", "emp_hardened"],
      tradeoffs: ["extreme_security", "massive_overhead"],
      icon: "flag",
    },
    {
      id: "a300-600st",
      name: "Airbus A300-600ST Beluga",
      rarity: "legendary",
      capacity: 0,
      range: 2779,
      cruiseSpeed: 420,
      maxAltitude: 35000,
      fuelBurn: 5800,
      purchasePrice: 0,
      monthlyMaintenance: 650000,
      description: "The whale of the sky. Carries complete A320 fuselage sections. That cockpit drop is iconic.",
      specialAbilities: ["outsize_cargo", "factory_logistics"],
      tradeoffs: ["cargo_only", "specialized"],
      icon: "package",
    },
    {
      id: "a330-743l",
      name: "Airbus A330-743L BelugaXL",
      rarity: "legendary",
      capacity: 0,
      range: 2500,
      cruiseSpeed: 430,
      maxAltitude: 35000,
      fuelBurn: 5500,
      purchasePrice: 0,
      monthlyMaintenance: 700000,
      description: "Even bigger whale. Can carry two A350 wings. The smile is mandatory.",
      specialAbilities: ["outsize_cargo", "smiling_livery"],
      tradeoffs: ["cargo_only", "very_specialized"],
      icon: "smile",
    },
    {
      id: "dreamlifter",
      name: "Boeing 747-400LCF Dreamlifter",
      rarity: "legendary",
      capacity: 0,
      range: 4445,
      cruiseSpeed: 470,
      maxAltitude: 43000,
      fuelBurn: 9500,
      purchasePrice: 0,
      monthlyMaintenance: 850000,
      description: "Modified 747 for 787 parts. Swing tail. 65,000 cubic feet. Once landed at the wrong airport.",
      specialAbilities: ["swing_tail", "massive_volume"],
      tradeoffs: ["cargo_only", "wrong_airport_history"],
      icon: "box",
    },
    {
      id: "sonic-cruiser",
      name: "Boeing Sonic Cruiser",
      rarity: "legendary",
      capacity: 250,
      range: 9000,
      cruiseSpeed: 590,
      maxAltitude: 45000,
      fuelBurn: 6500,
      purchasePrice: 0,
      monthlyMaintenance: 550000,
      description: "The 787 that should have been. Mach 0.98. Cancelled after 9/11. Delta canard design lives in our dreams.",
      specialAbilities: ["near_sonic", "fuel_efficient_fast"],
      tradeoffs: ["never_built", "concept_only"],
      icon: "fast-forward",
    },
    {
      id: "747x",
      name: "Boeing 747X",
      rarity: "legendary",
      capacity: 500,
      range: 8700,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 10000,
      purchasePrice: 0,
      monthlyMaintenance: 750000,
      description: "The stretched 747 that never was. Lost to the A380. Would have been magnificent.",
      specialAbilities: ["super_stretch", "747_heritage"],
      tradeoffs: ["never_built", "a380_killed_it"],
      icon: "maximize",
    },
    {
      id: "a380neo",
      name: "Airbus A380neo",
      rarity: "legendary",
      capacity: 615,
      range: 9200,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 9500,
      purchasePrice: 0,
      monthlyMaintenance: 950000,
      description: "The A380 refresh that Airbus studied but never built. New engines, 13% fuel savings. Emirates begged.",
      specialAbilities: ["neo_efficiency", "superjumbo"],
      tradeoffs: ["never_built", "market_killed_it"],
      icon: "refresh-cw",
    },
    {
      id: "boom-overture",
      name: "Boom Overture",
      rarity: "legendary",
      capacity: 80,
      range: 4888,
      cruiseSpeed: 1120,
      maxAltitude: 60000,
      fuelBurn: 12000,
      purchasePrice: 0,
      monthlyMaintenance: 900000,
      description: "Supersonic revival. Mach 1.7. United ordered 15. American ordered 20. Will it actually fly? TBD.",
      specialAbilities: ["supersonic_revival", "modern_sst"],
      tradeoffs: ["unproven", "sonic_boom_limits"],
      icon: "zap",
    },
    {
      id: "e-3a",
      name: "E-3A AWACS Sentry",
      rarity: "legendary",
      capacity: 30,
      range: 5000,
      cruiseSpeed: 470,
      maxAltitude: 42000,
      fuelBurn: 6500,
      purchasePrice: 0,
      monthlyMaintenance: 1200000,
      description: "707 with a 30-foot rotodome. Sees everything within 250 miles. The eye in the sky.",
      specialAbilities: ["awacs_radar", "command_control"],
      tradeoffs: ["military_only", "no_passengers"],
      icon: "radio",
    },
    {
      id: "e-4b",
      name: "E-4B Nightwatch",
      rarity: "legendary",
      capacity: 100,
      range: 7800,
      cruiseSpeed: 490,
      maxAltitude: 45000,
      fuelBurn: 9500,
      purchasePrice: 0,
      monthlyMaintenance: 2000000,
      description: "The Doomsday Plane. 747 command post. Hardened against nuclear EMP. Always one airborne.",
      specialAbilities: ["nuclear_hardened", "command_post", "always_airborne"],
      tradeoffs: ["classified", "doomsday_vibes"],
      icon: "shield",
    },
    {
      id: "c-5m",
      name: "C-5M Super Galaxy",
      rarity: "legendary",
      capacity: 0,
      range: 5524,
      cruiseSpeed: 450,
      maxAltitude: 35000,
      fuelBurn: 14000,
      purchasePrice: 0,
      monthlyMaintenance: 1100000,
      description: "Modernized C-5. Can carry 2 M1 Abrams tanks. 6 Apache helicopters. Or an entire F-16.",
      specialAbilities: ["massive_cargo", "outsize_military"],
      tradeoffs: ["cargo_only", "fuel_monster"],
      icon: "truck",
    },
    // ASCENDENT TIER - Cancelled dream projects, ultra-rare concepts
    {
      id: "a380-800f",
      name: "Airbus A380-800F",
      rarity: "ascendent",
      capacity: 0,
      range: 5600,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 11000,
      purchasePrice: 0,
      monthlyMaintenance: 1800000,
      description: "The freighter A380 that never flew. 150 tonnes of cargo. Orders collapsed. The dream that was too big.",
      specialAbilities: ["superjumbo_freight", "massive_volume"],
      tradeoffs: ["never_built"],
      icon: "package",
    },
    {
      id: "boeing-2707",
      name: "Boeing 2707 SST",
      rarity: "ascendent",
      capacity: 300,
      range: 4000,
      cruiseSpeed: 1800,
      maxAltitude: 70000,
      fuelBurn: 35000,
      purchasePrice: 0,
      monthlyMaintenance: 3000000,
      description: "America's supersonic dream. Mach 2.7. Variable sweep wings. Congress killed it 1971. What could have been.",
      specialAbilities: ["hypersonic_dream", "swing_wing"],
      tradeoffs: ["never_built", "fuel_nightmare"],
      icon: "rocket",
    },
    {
      id: "mrj90",
      name: "Mitsubishi SpaceJet M90",
      rarity: "ascendent",
      capacity: 88,
      range: 2040,
      cruiseSpeed: 480,
      maxAltitude: 39000,
      fuelBurn: 2200,
      purchasePrice: 0,
      monthlyMaintenance: 180000,
      description: "Japan's return to aviation. $10 billion spent. Zero delivered. Cancelled 2023. National heartbreak.",
      specialAbilities: ["japanese_precision", "modern_regional"],
      tradeoffs: ["cursed_program"],
      icon: "sunset",
    },
    {
      id: "md-12",
      name: "McDonnell Douglas MD-12",
      rarity: "legendary",
      capacity: 511,
      range: 7400,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 11500,
      purchasePrice: 0,
      monthlyMaintenance: 1500000,
      description: "Double-deck trijet. Would have beaten A380 by a decade. No airline committed. Douglas died instead.",
      specialAbilities: ["double_deck", "trijet_efficiency"],
      tradeoffs: ["never_built"],
      icon: "layers",
    },
    {
      id: "l-2000",
      name: "Lockheed L-2000",
      rarity: "ascendent",
      capacity: 270,
      range: 4000,
      cruiseSpeed: 1800,
      maxAltitude: 70000,
      fuelBurn: 32000,
      purchasePrice: 0,
      monthlyMaintenance: 2800000,
      description: "Lockheed's SST entry. Lost to Boeing 2707. Then Boeing lost to Congress. Double death.",
      specialAbilities: ["mach_3_capable", "lockheed_heritage"],
      tradeoffs: ["double_cancelled"],
      icon: "x",
    },
    {
      id: "a2000",
      name: "Airbus A2000",
      rarity: "ascendent",
      capacity: 656,
      range: 8500,
      cruiseSpeed: 490,
      maxAltitude: 43000,
      fuelBurn: 12000,
      purchasePrice: 0,
      monthlyMaintenance: 2000000,
      description: "1990s ultra-high-capacity study. 800 passengers in one variant. A380 was the compromise.",
      specialAbilities: ["ultra_capacity", "study_only"],
      tradeoffs: ["never_built"],
      icon: "maximize-2",
    },
    {
      id: "nma",
      name: "Boeing NMA 797",
      rarity: "ascendent",
      capacity: 270,
      range: 5000,
      cruiseSpeed: 520,
      maxAltitude: 43000,
      fuelBurn: 5500,
      purchasePrice: 0,
      monthlyMaintenance: 450000,
      description: "New Midsize Airplane. The 757/767 replacement. Delayed indefinitely. Boeing's lost decade continues.",
      specialAbilities: ["midsize_revolution", "757_replacement"],
      tradeoffs: ["perpetual_delay"],
      icon: "clock",
    },
    {
      id: "stratolaunch",
      name: "Stratolaunch Roc",
      rarity: "ascendent",
      capacity: 0,
      range: 2000,
      cruiseSpeed: 330,
      maxAltitude: 35000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 2500000,
      description: "Largest wingspan ever. 385 feet. Launches rockets. Paul Allen's dream. One of one.",
      specialAbilities: ["largest_wingspan", "rocket_launch_platform"],
      tradeoffs: ["not_for_passengers"],
      icon: "maximize",
    },
    {
      id: "vc-25-concept",
      name: "VC-25C Future Air Force One",
      rarity: "legendary",
      capacity: 80,
      range: 10000,
      cruiseSpeed: 600,
      maxAltitude: 50000,
      fuelBurn: 8000,
      purchasePrice: 0,
      monthlyMaintenance: 3500000,
      description: "Classified next-gen presidential transport. Stealth? Hypersonic? Nobody knows. Probably doesn't exist. Yet.",
      specialAbilities: ["classified_everything", "next_gen_everything"],
      tradeoffs: ["may_not_exist"],
      icon: "lock",
    },
    {
      id: "civil-c17",
      name: "Boeing C-17 Civilian",
      rarity: "ascendent",
      capacity: 0,
      range: 5200,
      cruiseSpeed: 450,
      maxAltitude: 45000,
      fuelBurn: 11000,
      purchasePrice: 0,
      monthlyMaintenance: 1200000,
      description: "Civilian cargo C-17 that FedEx and UPS begged for. Boeing said no. The plane that should exist.",
      specialAbilities: ["rough_field", "massive_cargo", "short_landing"],
      tradeoffs: ["boeing_refused"],
      icon: "truck",
    },
  ],

  // MYTHIC TIER (0.5% of rolls) - ONE AND ONLY
  // =========================================================================
  // There is ONE mythic aircraft: Ghost Apotheosis (Stealth 747)
  // It rotates monthly. Only one player per server per month can win it.
  // =========================================================================
  // MYTHIC NARRATIVE CONTEXT:
  // =========================================================================
  // Project Blackbird II is a classified, ultra-low-observable derivative of
  // the Boeing 747 platform. Officially it does not exist. Unofficially, it
  // operates above and around civilian airspace with minimal interaction,
  // reduced traceability, and atypical regulatory behavior.
  //
  // This aircraft is not faster because it is powerful - it is faster because
  // it is ignored.
  //
  // CORE IDENTITY: Breaks visibility and authority, not physics.
  // Blackbird II does not negate systems entirely. It reduces interaction with them.
  //
  // EXPLICIT NON-ABILITIES (IMPORTANT - DO NOT ADD):
  // - No weather immunity
  // - No maintenance immunity
  // - No slot immunity
  // - No crash protection
  // - No bypass of crew legality
  //
  // This aircraft is powerful because systems step aside, not because it
  // ignores reality.
  // =========================================================================
  mythic: [
    {
      id: "ghost-apotheosis",
      name: "Ghost Apotheosis",
      rarity: "mythic",
      capacity: 420,
      range: 8500,
      cruiseSpeed: 588,       // +20% over standard 490 (optimized routing, not thrust)
      maxAltitude: 45000,
      fuelBurn: 11000,
      purchasePrice: 0,
      monthlyMaintenance: 2500000,
      description: "Stealth 747. Classified ultra-low-observable derivative. Officially does not exist. Powerful because systems step aside, not because it ignores reality.",
      specialAbilities: [
        "radar_invisibility",     // Does not appear on standard ATC radar layers
        "reduced_atc_interaction",// Fewer ATC events, lower reroute/hold/slot conflict chance
        "enhanced_cruise",        // +20% cruise speed via optimized routing and clearance
        "fare_multiplier",        // Premium fares from exclusivity/secrecy/prestige
      ],
      tradeoffs: [
        "one_per_server_per_month", // Only one player can win per server per month
        "high_maintenance",         // Classified tech requires specialized upkeep
      ],
      // Tone: Dead-serious, Cold War / Area 51 / Skunk Works
      icon: "ghost",
    },
  ],
};

// ============================================================================
// EOS STATE CREATION
// ============================================================================
export function createEOSState() {
  return {
    hasEOS: false,
    consecutiveDays: 0,
    qualificationStart: null,
    grantedAt: null,
    lostAt: null,
    lockoutUntil: null,
    // Tracking for collapse detection
    recentDiversions: [],    // { timestamp, flightId }
    recentCancellations: [], // { timestamp, flightId }
    // Daily tracking
    dailyChecks: [],         // Last 14 days of requirement checks
    // Stats
    timesEarned: 0,
    timesLost: 0,
    totalDaysHeld: 0,
    rareAircraftRolls: 0,
    rareAircraftOwned: [],   // Array of rare aircraft IDs earned
  };
}

// ============================================================================
// CHECK IF PLAYER MEETS EOS REQUIREMENTS FOR TODAY
// ============================================================================
export function checkDailyEOSRequirements(worldState) {
  const rep = worldState.reputation || {};
  const airline = worldState.airline || {};
  const flights = worldState.flights || [];
  const aircraft = worldState.aircraft || [];

  const results = {
    passed: true,
    checks: {},
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ONE-TIME GATES (Must be met to START attempting EOS, don't break streak)
  // These are tracked for display but do NOT affect results.passed
  // ═══════════════════════════════════════════════════════════════════════════

  // GATE 1: Tier 5 / RepScore ≥ 15,000
  const repScore = rep.currentScore || 0;
  results.checks.repScore = {
    required: EOS_REQUIREMENTS.MIN_REP_SCORE,
    actual: repScore,
    passed: repScore >= EOS_REQUIREMENTS.MIN_REP_SCORE,
    isGate: true, // One-time gate, not daily requirement
  };
  // Does NOT block results.passed - it's a one-time gate

  // GATE 2: Minimum fleet size (30 aircraft)
  const fleetSize = aircraft.length;
  results.checks.fleetSize = {
    required: EOS_REQUIREMENTS.MIN_FLEET_SIZE,
    actual: fleetSize,
    passed: fleetSize >= EOS_REQUIREMENTS.MIN_FLEET_SIZE,
    isGate: true, // One-time gate, not daily requirement
  };
  // Does NOT block results.passed - it's a one-time gate

  // ═══════════════════════════════════════════════════════════════════════════
  // DAILY STREAK METRICS (These 4 metrics control the 10-day streak counter)
  // ALL 4 must be met every day to increment streak. Missing ANY resets to 0.
  // ═══════════════════════════════════════════════════════════════════════════

  // DAILY 1: OTP Arrivals ≥ 85% (rolling 7-day)
  const gameNow = worldState.gameTime ? new Date(worldState.gameTime).getTime() : Date.now();
  const last7DaysFlights = flights.filter(f => {
    if (f.phase !== 'completed') return false;
    const completedAt = new Date(f.completedAt || f.actualArrival || f.arrivalTime);
    const daysAgo = (gameNow - completedAt.getTime()) / (24 * 60 * 60 * 1000);
    return daysAgo <= 7;
  });

  const onTimeArrivals = last7DaysFlights.filter(f => (f.totalDelayMinutes || 0) <= 14).length;
  const otpRate = last7DaysFlights.length > 0 ? onTimeArrivals / last7DaysFlights.length : 1;

  results.checks.otpArrivals = {
    required: EOS_REQUIREMENTS.MIN_OTP_ARRIVALS,
    actual: otpRate,
    actualPercent: Math.round(otpRate * 100),
    totalFlights: last7DaysFlights.length,
    onTimeFlights: onTimeArrivals,
    passed: otpRate >= EOS_REQUIREMENTS.MIN_OTP_ARRIVALS,
    isDaily: true, // Daily streak requirement
  };
  if (!results.checks.otpArrivals.passed) results.passed = false;

  // DAILY 2: Zero cancellations today
  const todayCancellations = (worldState.stats?.dailyCancellations || 0);
  results.checks.cancellations = {
    required: EOS_REQUIREMENTS.MAX_CANCELLATIONS,
    actual: todayCancellations,
    passed: todayCancellations === 0,
    isDaily: true, // Daily streak requirement
  };
  if (!results.checks.cancellations.passed) results.passed = false;

  // DAILY 3: Zero safety incidents today
  const eos = worldState.eos || {};
  const lastSafetyBaseline = eos.lastSafetyIncidents ?? (rep.stats?.safetyIncidents || 0);
  const currentSafetyIncidents = rep.stats?.safetyIncidents || 0;
  const todaySafetyIncidents = currentSafetyIncidents - lastSafetyBaseline;
  results.checks.safetyIncidents = {
    required: EOS_REQUIREMENTS.MAX_SAFETY_INCIDENTS,
    actual: Math.max(0, todaySafetyIncidents),
    passed: todaySafetyIncidents <= 0,
    isDaily: true, // Daily streak requirement
  };
  if (!results.checks.safetyIncidents.passed) results.passed = false;

  // DAILY 4: Positive net cashflow
  const dailyCashflow = (worldState.stats?.dailyRevenue || 0) - (worldState.stats?.dailyCosts || 0);
  results.checks.cashflow = {
    required: EOS_REQUIREMENTS.MIN_NET_CASHFLOW,
    actual: dailyCashflow,
    passed: dailyCashflow >= 0,
    isDaily: true, // Daily streak requirement
  };
  if (!results.checks.cashflow.passed) results.passed = false;

  // DAILY 5: Minimum flights initiated per day (20+ departures required)
  // Uses departures (not completions) because long-haul flights may take multiple days
  const todayDepartures = worldState.stats?.dailyDepartures || 0;
  results.checks.minFlights = {
    required: EOS_REQUIREMENTS.MIN_FLIGHTS_PER_DAY,
    actual: todayDepartures,
    passed: todayDepartures >= EOS_REQUIREMENTS.MIN_FLIGHTS_PER_DAY,
    isDaily: true, // Daily streak requirement
  };
  if (!results.checks.minFlights.passed) results.passed = false;

  return results;
}

// NOTE: Collapse system removed - EOS is PERMANENT in Simple Mode.
// Collapse/degradation is a Simulation Mode feature (not yet implemented).

// ============================================================================
// PROCESS EOS DAILY CHECK (CALLED AT MIDNIGHT)
// ============================================================================
export function processEOSDaily(worldState, gameDate) {
  const eos = worldState.eos || createEOSState();
  const newEOS = { ...eos };

  // Check if in cooldown
  if (newEOS.lockoutUntil) {
    const lockoutEnd = new Date(newEOS.lockoutUntil);
    if (new Date(gameDate) < lockoutEnd) {
      // Still in cooldown
      return {
        eos: newEOS,
        events: [{
          type: "EOS_COOLDOWN",
          message: `EOS locked out until ${lockoutEnd.toLocaleDateString()}`,
        }],
      };
    } else {
      // Cooldown expired
      newEOS.lockoutUntil = null;
    }
  }

  const events = [];

  // If already has EOS, it's permanent (no collapse system)
  // EOS is a permanent achievement once earned
  if (newEOS.hasEOS) {
    // EOS maintained permanently - increment total days held
    newEOS.totalDaysHeld++;
    // No events needed for maintenance - it's permanent now
    return { eos: newEOS, events: [], unlocks: {} };
  }

  // Not EOS yet - check daily requirements
  const dailyCheck = checkDailyEOSRequirements(worldState);

  // DEBUG: Log EOS daily check results with BIG VISIBLE BANNER
  debug('%c═══════════════════════════════════════════════════════════════', 'color: #ff00ff; font-weight: bold;');
  debug('%c                    🎯 EOS DAILY CHECK 🎯                      ', 'color: #ff00ff; font-weight: bold; font-size: 14px;');
  debug('%c═══════════════════════════════════════════════════════════════', 'color: #ff00ff; font-weight: bold;');
  debug(`%c  Date: ${gameDate}`, 'color: #ffff00;');
  debug(`%c  STREAK PASSED: ${dailyCheck.passed ? '✅ YES' : '❌ NO'}`, dailyCheck.passed ? 'color: #00ff00; font-weight: bold;' : 'color: #ff0000; font-weight: bold;');
  debug(`%c  ─────────────────────────────────────────────────────────────`, 'color: #888;');
  debug(`%c  ONE-TIME GATES (don't break streak):`, 'color: #888;');
  debug(`%c    Tier 5:     ${dailyCheck.checks.repScore?.actual || 0}/15000  ${dailyCheck.checks.repScore?.passed ? '✅' : '⏳'}`, dailyCheck.checks.repScore?.passed ? 'color: #00ff00;' : 'color: #ffaa00;');
  debug(`%c    Fleet:      ${dailyCheck.checks.fleetSize?.actual || 0}/30    ${dailyCheck.checks.fleetSize?.passed ? '✅' : '⏳'}`, dailyCheck.checks.fleetSize?.passed ? 'color: #00ff00;' : 'color: #ffaa00;');
  debug(`%c  ─────────────────────────────────────────────────────────────`, 'color: #888;');
  debug(`%c  DAILY STREAK METRICS (all 5 required):`, 'color: #00ffff;');
  debug(`%c    OTP (7-day): ${dailyCheck.checks.otpArrivals?.actualPercent || 0}%/85%  ${dailyCheck.checks.otpArrivals?.passed ? '✅' : '❌'}`, dailyCheck.checks.otpArrivals?.passed ? 'color: #00ff00;' : 'color: #ff0000;');
  debug(`%c    Cancellations: ${dailyCheck.checks.cancellations?.actual || 0}/0    ${dailyCheck.checks.cancellations?.passed ? '✅' : '❌'}`, dailyCheck.checks.cancellations?.passed ? 'color: #00ff00;' : 'color: #ff0000;');
  debug(`%c    Safety:     ${dailyCheck.checks.safetyIncidents?.actual || 0}/0    ${dailyCheck.checks.safetyIncidents?.passed ? '✅' : '❌'}`, dailyCheck.checks.safetyIncidents?.passed ? 'color: #00ff00;' : 'color: #ff0000;');
  debug(`%c    Cashflow:   $${dailyCheck.checks.cashflow?.actual || 0}  ${dailyCheck.checks.cashflow?.passed ? '✅' : '❌'}`, dailyCheck.checks.cashflow?.passed ? 'color: #00ff00;' : 'color: #ff0000;');
  debug(`%c    Flights:    ${dailyCheck.checks.minFlights?.actual || 0}/20   ${dailyCheck.checks.minFlights?.passed ? '✅' : '❌'}`, dailyCheck.checks.minFlights?.passed ? 'color: #00ff00;' : 'color: #ff0000;');
  debug(`%c  ─────────────────────────────────────────────────────────────`, 'color: #888;');
  debug(`%c  CONSECUTIVE DAYS: ${newEOS.consecutiveDays}/${EOS_REQUIREMENTS.QUALIFICATION_DAYS}`, 'color: #00ffff; font-weight: bold;');
  debug('%c═══════════════════════════════════════════════════════════════', 'color: #ff00ff; font-weight: bold;');

  // Store the check
  newEOS.dailyChecks = [...(newEOS.dailyChecks || []).slice(-13), {
    date: gameDate,
    passed: dailyCheck.passed,
    checks: dailyCheck.checks,
  }];

  // Update safety incidents baseline for next day's comparison
  const rep = worldState.reputation || {};
  newEOS.lastSafetyIncidents = rep.stats?.safetyIncidents || 0;
  newEOS.lastCancellations = worldState.stats?.cancelledFlights || 0;

  // Track unlocks to grant (permanent, never removed)
  const unlocks = {};
  const currentUnlocks = worldState.unlocks || {};

  if (dailyCheck.passed) {
    // Good day - increment counter
    if (newEOS.consecutiveDays === 0) {
      newEOS.qualificationStart = gameDate;
    }
    newEOS.consecutiveDays++;

    events.push({
      type: "EOS_PROGRESS",
      message: `EOS qualification day ${newEOS.consecutiveDays}/${EOS_REQUIREMENTS.QUALIFICATION_DAYS}`,
      checks: dailyCheck.checks,
    });

    // NOTE: 4x speed is now available from game start (no longer a Day 4 unlock)
    // 12x speed remains the EOS achievement reward

    // Check if qualified (10 days complete + both gates required for actual grant)
    if (newEOS.consecutiveDays >= EOS_REQUIREMENTS.QUALIFICATION_DAYS) {
      // Must have BOTH one-time gates to actually receive EOS
      const hasTier5 = dailyCheck.checks.repScore?.passed;
      const hasFleet30 = dailyCheck.checks.fleetSize?.passed;

      if (hasTier5 && hasFleet30) {
        newEOS.hasEOS = true;
        newEOS.grantedAt = gameDate;
        newEOS.timesEarned++;

        events.push({
          type: "EOS_GRANTED",
          severity: "success",
          message: "Elite Operator Status granted.",
        });

        // SPEED UNLOCK: 12x on EOS achieved (permanent, one-time)
        if (!currentUnlocks.speed12x) {
          unlocks.speed12x = true;
          events.push({
            type: "SPEED_12X_UNLOCKED",
            severity: "success",
            message: "EOS achieved. 12x speed unlocked. Now go chase that Mythic.",
            isToast: true,
          });
        }
      } else {
        // Days complete but waiting for gates
        const missing = [];
        if (!hasTier5) missing.push("Tier 5");
        if (!hasFleet30) missing.push("30 aircraft");
        events.push({
          type: "EOS_WAITING_GATES",
          severity: "info",
          message: `${EOS_REQUIREMENTS.QUALIFICATION_DAYS} days complete - need ${missing.join(" and ")} to activate EOS!`,
        });
      }
    }
  } else {
    // Failed day - reset counter
    if (newEOS.consecutiveDays > 0) {
      events.push({
        type: "EOS_RESET",
        severity: "warning",
        message: `EOS qualification reset (was ${newEOS.consecutiveDays} days)`,
        failedChecks: Object.entries(dailyCheck.checks)
          .filter(([_, v]) => !v.passed)
          .map(([k, v]) => ({ check: k, ...v })),
      });
    }
    newEOS.consecutiveDays = 0;
    newEOS.qualificationStart = null;
  }

  return { eos: newEOS, events, unlocks };
}

// ============================================================================
// RECORD DIVERSION (FOR COLLAPSE TRACKING)
// ============================================================================
export function recordDiversion(eosState, flightId, gameTime = null) {
  const newState = { ...eosState };
  // Use game time if provided, otherwise fall back to real time
  const timestamp = gameTime ? new Date(gameTime).toISOString() : new Date().toISOString();
  newState.recentDiversions = [
    ...(newState.recentDiversions || []),
    { timestamp, flightId },
  ].slice(-10); // Keep last 10
  return newState;
}

// ============================================================================
// RECORD CANCELLATION (FOR COLLAPSE TRACKING)
// ============================================================================
export function recordCancellation(eosState, flightId, gameTime = null) {
  const newState = { ...eosState };
  // Use game time if provided, otherwise fall back to real time
  const timestamp = gameTime ? new Date(gameTime).toISOString() : new Date().toISOString();
  newState.recentCancellations = [
    ...(newState.recentCancellations || []),
    { timestamp, flightId },
  ].slice(-10); // Keep last 10
  return newState;
}

// ============================================================================
// RARE AIRCRAFT ROLL SYSTEM
// ============================================================================

/**
 * Core roll function - determines rarity based on probabilities
 *
 * WHEN MYTHIC IS AVAILABLE (not yet claimed this month):
 *   Mythic: 0.1% | Legendary: 4.9% | Anomalous: 5% | Relic: 10% | Rare: 80%
 *
 * WHEN MYTHIC IS CLAIMED (Ghost Apotheosis won this month):
 *   Mythic tier DOES NOT EXIST - it's gone from the table entirely
 *   Legendary: 5% | Anomalous: 5% | Relic: 10% | Rare: 80%
 *
 * @returns {Object} Roll result with rarity, aircraft, and roll info
 */
export function rollRareAircraft() {
  const roll = Math.random();
  const mythicAvailable = isMythicAvailable();

  let rarity;

  if (mythicAvailable) {
    // FULL TABLE - Mythic exists at 0.1%
    // Cumulative: 0.1% -> 5% -> 10% -> 20% -> 100%
    const mythicCutoff = 0.001;      // 0.1%
    const legendaryCutoff = 0.05;    // 5.0% (0.1% + 4.9%)
    const anomalousCutoff = 0.10;    // 10.0% (5% + 5%)
    const relicCutoff = 0.20;        // 20.0% (10% + 10%)

    if (roll < mythicCutoff) {
      rarity = "mythic";
    } else if (roll < legendaryCutoff) {
      rarity = "legendary";
    } else if (roll < anomalousCutoff) {
      rarity = "anomalous";
    } else if (roll < relicCutoff) {
      rarity = "relic";
    } else {
      rarity = "rare";
    }
  } else {
    // MYTHIC CLAIMED - Mythic tier GONE, legendary absorbs the 0.1%
    // Cumulative: 5% -> 10% -> 20% -> 100%
    const legendaryCutoff = 0.05;    // 5.0% (4.9% + mythic's 0.1%)
    const anomalousCutoff = 0.10;    // 10.0%
    const relicCutoff = 0.20;        // 20.0%

    if (roll < legendaryCutoff) {
      rarity = "legendary";
    } else if (roll < anomalousCutoff) {
      rarity = "anomalous";
    } else if (roll < relicCutoff) {
      rarity = "relic";
    } else {
      rarity = "rare";
    }
  }

  // Pick random aircraft from that rarity
  const pool = RARE_AIRCRAFT[rarity];
  const aircraft = pool[Math.floor(Math.random() * pool.length)];

  return {
    rarity,
    aircraft: { ...aircraft },
    roll: roll,
    rollPercent: (roll * 100).toFixed(3),
    mythicWasAvailable: mythicAvailable,
  };
}

// ============================================================================
// ONE-PER-SAVE MYTHIC CHECK
// ============================================================================

/**
 * Check if player already owns a mythic aircraft
 * @param {Array} ownedAircraft - Player's current aircraft fleet
 * @returns {boolean} - True if player already has a mythic
 */
export function hasMythicAircraft(ownedAircraft) {
  if (!ownedAircraft || !Array.isArray(ownedAircraft)) return false;
  return ownedAircraft.some(a => a.rarity === "mythic");
}

/**
 * Roll for rare aircraft, respecting one-mythic-per-save rule
 * If player already has mythic, mythic tier is excluded (reroll until non-mythic)
 * @param {Array} ownedAircraft - Player's current aircraft fleet
 * @returns {Object} - Roll result with rarity and aircraft
 */
export function rollRareAircraftWithMythicCheck(ownedAircraft) {
  // If player already has a mythic, reroll any mythic results
  if (hasMythicAircraft(ownedAircraft)) {
    let result = rollRareAircraft();
    // Reroll if we hit mythic (extremely rare edge case)
    while (result.rarity === "mythic") {
      debug("[EOS] Player already has mythic - rerolling...");
      result = rollRareAircraft();
    }
    return result;
  }

  return rollRareAircraft();
}

// ============================================================================
// PROCESS A380 PURCHASE (TRIGGERS ROLL IF EOS)
// ============================================================================
export function processA380Purchase(worldState) {
  const eos = worldState.eos || createEOSState();

  // Must have EOS to roll
  if (!eos.hasEOS) {
    return {
      success: false,
      message: "Elite Operator Status required for rare aircraft rolls",
      rollTriggered: false,
    };
  }

  // Perform the roll (with one-per-save mythic check)
  const ownedAircraft = worldState.aircraft || [];
  const rollResult = rollRareAircraftWithMythicCheck(ownedAircraft);

  // If player won the MYTHIC, claim it for this month!
  // This removes Ghost Apotheosis from the roll table for everyone else
  let mythicClaim = null;
  if (rollResult.rarity === "mythic") {
    const airlineName = worldState.airline?.name || "Unknown Airline";
    const playerId = worldState.airline?.id || "unknown";
    mythicClaim = claimMythic(airlineName, playerId);
    debug(`[EOS] !!!! MYTHIC WON !!!! ${airlineName} has claimed the Ghost Apotheosis!`);
  }

  // Update EOS state
  const newEOS = {
    ...eos,
    rareAircraftRolls: (eos.rareAircraftRolls || 0) + 1,
    rareAircraftOwned: [
      ...(eos.rareAircraftOwned || []),
      {
        aircraftId: rollResult.aircraft.id,
        rarity: rollResult.rarity,
        earnedAt: new Date().toISOString(),
        roll: rollResult.roll,
      },
    ],
  };

  // Create the actual aircraft to add to fleet
  const rareAircraft = {
    id: `RARE-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    aircraftId: rollResult.aircraft.id, // Original collectible ID for image lookup (e.g., "747-9")
    type: rollResult.aircraft.id,
    model: rollResult.aircraft.id,
    name: rollResult.aircraft.name,
    registration: `N${Math.floor(Math.random() * 900) + 100}${rollResult.rarity.toUpperCase().slice(0, 2)}`,
    rarity: rollResult.rarity,
    capacity: rollResult.aircraft.capacity,
    range: rollResult.aircraft.range,
    cruiseSpeed: rollResult.aircraft.cruiseSpeed,
    maxAltitude: rollResult.aircraft.maxAltitude,
    fuelBurn: rollResult.aircraft.fuelBurn,
    monthlyMaintenance: rollResult.aircraft.monthlyMaintenance,
    specialAbilities: rollResult.aircraft.specialAbilities,
    tradeoffs: rollResult.aircraft.tradeoffs,
    description: rollResult.aircraft.description,
    icon: rollResult.aircraft.icon,
    status: "available",
    location: worldState.airline?.hub || "KPDX",
    homeBase: worldState.airline?.hub || "KPDX",
    flightHours: 0,
    cycles: 0,
    leased: false,
    purchasePrice: 0, // Earned, not bought
    acquiredAt: new Date().toISOString(),
    isRareAircraft: true,
  };

  // Build the success message
  let message = `Roll result: ${rollResult.aircraft.name} (${rollResult.rarity.toUpperCase()})`;
  if (rollResult.rarity === "mythic") {
    message = `Mythic roll: Ghost Apotheosis. This aircraft is removed from the roll table for ${getCurrentMythicMonth()}.`;
  }

  return {
    success: true,
    rollTriggered: true,
    rollResult,
    rareAircraft,
    newEOS,
    mythicClaim,
    message,
  };
}

// ============================================================================
// GET EOS STATUS INFO
// ============================================================================
export function getEOSStatus(worldState) {
  const eos = worldState.eos || createEOSState();

  // Check if in cooldown
  let cooldownRemaining = 0;
  if (eos.lockoutUntil) {
    const now = new Date(worldState.gameTime || Date.now());
    const lockoutEnd = new Date(eos.lockoutUntil);
    cooldownRemaining = Math.max(0, Math.ceil((lockoutEnd - now) / (24 * 60 * 60 * 1000)));
  }

  // Check current daily requirements
  const dailyCheck = checkDailyEOSRequirements(worldState);

  return {
    hasEOS: eos.hasEOS,
    consecutiveDays: eos.consecutiveDays,
    daysRequired: EOS_REQUIREMENTS.QUALIFICATION_DAYS,
    progress: eos.consecutiveDays / EOS_REQUIREMENTS.QUALIFICATION_DAYS,
    inCooldown: cooldownRemaining > 0,
    cooldownRemaining,
    lockoutUntil: eos.lockoutUntil,
    todayPassing: dailyCheck.passed,
    todayChecks: dailyCheck.checks,
    stats: {
      timesEarned: eos.timesEarned,
      timesLost: eos.timesLost,
      totalDaysHeld: eos.totalDaysHeld,
      rareAircraftRolls: eos.rareAircraftRolls,
      rareAircraftOwned: eos.rareAircraftOwned?.length || 0,
    },
    rareAircraft: eos.rareAircraftOwned || [],
  };
}

// ============================================================================
// EXPORTS
// ============================================================================
export default {
  createEOSState,
  checkDailyEOSRequirements,
  processEOSDaily,
  recordDiversion,
  recordCancellation,
  rollRareAircraft,
  hasMythicAircraft,
  rollRareAircraftWithMythicCheck,
  processA380Purchase,
  getEOSStatus,
  // Mythic claim system
  getCurrentMythicMonth,
  getMythicClaimStatus,
  isMythicAvailable,
  claimMythic,
  getMonthlyMythicWinner,
  // Constants
  EOS_REQUIREMENTS,
  ROLL_PROBABILITIES,
  RARE_AIRCRAFT,
};
